import React, { useRef, useState, useEffect } from 'react'
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  MoreHorizontal,
  Home,
  FileText,
  CheckCircle,
  Database,
  Settings,
  AlertCircle,
  Microscope,
  StickyNote,
  //List
} from 'lucide-react'

export interface Tab {
  id: string
  label: string
  type: 'home' | 'validation' | 'data' | 'transactions' | 'settings' | 'file' | 'summary' | 'flag' | 'research' | 'notes' | 'json-extracted' | 'json-annotations'
  closable?: boolean
  iconName?: string
}

const getIconComponent = (iconName?: string) => {
  if (!iconName) return null
  
  switch (iconName) {
    case 'Home':
      return <Home className="w-4 h-4" />
    case 'FileText':
      return <FileText className="w-4 h-4" />
    case 'CheckCircle':
      return <CheckCircle className="w-4 h-4" />
    case 'Database':
      return <Database className="w-4 h-4" />
    case 'Settings':
      return <Settings className="w-4 h-4" />
    case 'AlertCircle':
      return <AlertCircle className="w-4 h-4" />
    case 'Microscope':
      return <Microscope className="w-4 h-4" />
    case 'Notes':
      return <StickyNote className="w-4 h-4" />
    default:
      return null
  }
}

interface TabBarProps {
  tabs: Tab[]
  activeTabId: string
  onTabClick: (tabId: string) => void
  onTabClose?: (tabId: string) => void
}

const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  // Check scroll position and update navigation buttons
  const checkScroll = () => {
    const container = scrollContainerRef.current
    if (!container) return

    setCanScrollLeft(container.scrollLeft > 0)
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    )
  }

  // Scroll to active tab when it changes
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const activeTabElement = container.querySelector(`[data-tab-id="${activeTabId}"]`)
    if (activeTabElement) {
      activeTabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
    checkScroll()
  }, [activeTabId])

  // Check scroll on mount and resize
  useEffect(() => {
    checkScroll()
    window.addEventListener('resize', checkScroll)
    return () => window.removeEventListener('resize', checkScroll)
  }, [tabs])

  const scrollTabs = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 200
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    })
  }

  // Track if tabs overflow (for future use)
  // const [hasOverflow, setHasOverflow] = useState(false)
  
  // useEffect(() => {
  //   const container = scrollContainerRef.current
  //   if (!container) return
  //   setHasOverflow(container.scrollWidth > container.clientWidth)
  // }, [tabs])

  return (
    <div className="flex items-center border-b border-gray-200 bg-gray-50 relative h-10">
      {/* Left scroll button */}
      {canScrollLeft && (
        <button
          onClick={() => scrollTabs('left')}
          className="flex-shrink-0 px-2 h-full hover:bg-gray-100 border-r border-gray-200 transition-colors"
          aria-label="Scroll tabs left"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
      )}

      {/* Scrollable tabs container */}
      <div 
        ref={scrollContainerRef}
        onScroll={checkScroll}
        className="flex-1 flex overflow-x-auto scrollbar-hide scroll-smooth h-full"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        } as React.CSSProperties}
      >
        <div className="flex">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              data-tab-id={tab.id}
              className={`group flex items-center px-3 h-10 cursor-pointer transition-all whitespace-nowrap flex-shrink-0 border-r border-gray-200 ${
                activeTabId === tab.id
                  ? 'bg-white border-t-2 border-t-orange-500 -mb-px'
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => onTabClick(tab.id)}
            >
              <div className="flex items-center space-x-1.5">
                {getIconComponent(tab.iconName)}
                <span className="text-sm truncate max-w-[120px]">
                  {tab.label}
                </span>
              </div>
              {tab.closable !== false && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onTabClose?.(tab.id)
                  }}
                  className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-gray-200 rounded transition-opacity ml-1.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Right scroll button */}
      {canScrollRight && (
        <button
          onClick={() => scrollTabs('right')}
          className="flex-shrink-0 px-2 h-full hover:bg-gray-100 border-l border-gray-200 transition-colors"
          aria-label="Scroll tabs right"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      )}

      {/* Overflow menu for many tabs */}
      {tabs.length > 5 && (
        <div className="relative flex-shrink-0 h-full">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="px-3 h-full hover:bg-gray-100 border-l border-gray-200 transition-colors"
            aria-label="Show all tabs"
          >
            <MoreHorizontal className="w-4 h-4 text-gray-600" />
          </button>
          
          {showDropdown && (
            <>
              {/* Backdrop to close dropdown */}
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowDropdown(false)}
              />
              
              {/* Dropdown menu */}
              <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">
                    All Open Tabs ({tabs.length})
                  </div>
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        onTabClick(tab.id)
                        setShowDropdown(false)
                      }}
                      className={`w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-gray-100 transition-colors ${
                        activeTabId === tab.id ? 'bg-orange-50 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        {getIconComponent(tab.iconName)}
                        <span className="text-sm truncate">{tab.label}</span>
                      </div>
                      {tab.closable !== false && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onTabClose?.(tab.id)
                          }}
                          className="p-0.5 hover:bg-gray-200 rounded flex-shrink-0 ml-2"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default TabBar
